import React, { useEffect, useState } from "react";
import { Vector3 } from "three";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import ModalModelViewer from "./modal-model-viewer";
import { Stack, Button, Dialog, Typography } from "@mui/material";
import { DepthEstimationStatus } from "../../../../context/model-viewer-context";
import ModelViewerControlBar from "./model-viewer-control-bar";

interface ModelViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageSources: string[];
  patientId: string;
  currentImageIndex: number;
  onImageIndexChange: (index: number) => void;
  height?: number;
}

const ModelViewerModal: React.FC<ModelViewerModalProps> = ({
  isOpen,
  onClose,
  imageSources,
  patientId,
  currentImageIndex,
  onImageIndexChange,
  height = 80,
}) => {
  const currentImage =
    currentImageIndex > -1 && currentImageIndex < imageSources.length
      ? imageSources[currentImageIndex]
      : null;

  const currentUrl =
    currentImageIndex < 0 || currentImageIndex >= imageSources.length
      ? ""
      : imageSources[currentImageIndex];

  const title = "Model Preview";

  // Calculate 16:9 aspect ratio dimensions - use viewport height as base
  const modalHeight = Math.min(height, 90); // Cap at 90vh to ensure it fits
  const modalWidth = (modalHeight * 16) / 9; // 16:9 aspect ratio

  const heightSize = modalHeight + "vh";
  const widthSize = modalWidth + "vh"; // Use vh for both to maintain ratio

  // Per-image data storage
  interface ImageMeasurementData {
    points: Vector3[];
    distance: number;
    angle: number;
    area: number;
    perimeter: number;
    depthEstimation: number;
    depthStatus: DepthEstimationStatus;
    showAreaTool: boolean;
    cameraPosition?: Vector3;
    cameraTarget?: Vector3;
  }

  const [imageDataMap, setImageDataMap] = useState<Map<string, ImageMeasurementData>>(new Map());

  // Current measurement state (derived from current image data)
  const currentImageData = imageDataMap.get(currentUrl) || {
    points: [],
    distance: -1,
    angle: -1,
    area: -1,
    perimeter: -1,
    depthEstimation: -1,
    depthStatus: DepthEstimationStatus.NOT_STARTED,
    showAreaTool: false,
    cameraPosition: undefined,
    cameraTarget: undefined,
  };

  const [showAreaTool, setShowAreaTool] = useState(currentImageData.showAreaTool);
  const [points, setPoints] = useState<Vector3[]>(currentImageData.points);
  const [distance, setDistance] = useState(currentImageData.distance);
  const [angle, setAngle] = useState(currentImageData.angle);
  const [area, setArea] = useState(currentImageData.area);
  const [perimeter, setPerimeter] = useState(currentImageData.perimeter);
  const [depthEstimation, setDepthEstimation] = useState(currentImageData.depthEstimation);
  const [depthStatus, setDepthStatus] = useState(currentImageData.depthStatus);
  const [currentCameraPosition, setCurrentCameraPosition] = useState<Vector3 | undefined>(currentImageData.cameraPosition);
  const [currentCameraTarget, setCurrentCameraTarget] = useState<Vector3 | undefined>(currentImageData.cameraTarget);

  const acceptDownload = () => {
    if (currentImage) {
      // You can customize the filename as needed
      downloadResource(
        currentImage,
        `${patientId} model ${currentImageIndex + 1}.ply`
      );
    }
  };

  const forceDownload = (blobUrl: string, filename: string) => {
    const a = document.createElement("a");
    a.download = filename;
    a.href = blobUrl;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const downloadResource = (url: string, filename: string) => {
    fetch(url, { mode: "cors" })
      .then((response) => response.blob())
      .then((blob) => {
        const blobUrl = window.URL.createObjectURL(blob);
        forceDownload(blobUrl, filename);
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
      })
      .catch((e) => console.error(e));
  };

  const onNavigateNext = () => {
    if (imageSources.length < 2) {
      return;
    }

    if (currentImageIndex + 1 < imageSources.length) {
      onImageIndexChange(currentImageIndex + 1);
    } else {
      onImageIndexChange(0);
    }
  };

  const onNavigatePrevious = () => {
    if (imageSources.length < 2) {
      return;
    }

    if (currentImageIndex - 1 >= 0) {
      onImageIndexChange(currentImageIndex - 1);
    } else {
      onImageIndexChange(imageSources.length - 1);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          onNavigatePrevious();
          break;
        case 'ArrowRight':
          event.preventDefault();
          onNavigateNext();
          break;
        case 'Escape':
          event.preventDefault();
          onClose();
          break;
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onNavigateNext, onNavigatePrevious, onClose]);

  // Save data when switching away from current image
  const [previousUrl, setPreviousUrl] = useState<string>("");

  useEffect(() => {
    if (previousUrl && previousUrl !== currentUrl) {
      saveCurrentImageData(previousUrl);
    }
    if (currentUrl !== previousUrl) {
      loadImageData(currentUrl);
      setPreviousUrl(currentUrl);
    }
  }, [currentUrl, previousUrl]);

  // Save data when modal closes
  useEffect(() => {
    if (!isOpen && currentUrl) {
      saveCurrentImageData(currentUrl);
    }
  }, [isOpen]);

  // Auto-save when measurement data changes
  useEffect(() => {
    if (currentUrl) {
      saveCurrentImageData(currentUrl);
    }
  }, [points, distance, angle, area, perimeter, depthEstimation, depthStatus, showAreaTool, currentCameraPosition, currentCameraTarget, currentUrl]);

  // Wrapper functions that auto-save data
  const updatePoints = (newPoints: Vector3[]) => {
    setPoints(newPoints);
    // Auto-save will happen on next render via saveCurrentImageData
  };

  const updateDistance = (newDistance: number) => {
    setDistance(newDistance);
  };

  const updateAngle = (newAngle: number) => {
    setAngle(newAngle);
  };

  const updateArea = (newArea: number) => {
    setArea(newArea);
  };

  const updatePerimeter = (newPerimeter: number) => {
    setPerimeter(newPerimeter);
  };

  const updateDepthEstimation = (newDepth: number) => {
    setDepthEstimation(newDepth);
  };

  const updateDepthStatus = (newStatus: DepthEstimationStatus) => {
    setDepthStatus(newStatus);
  };

  const updateShowAreaTool = (show: boolean) => {
    setShowAreaTool(show);
  };

  const handleCameraChange = (position: Vector3, target: Vector3) => {
    setCurrentCameraPosition(position);
    setCurrentCameraTarget(target);
  };

  // Model viewer control handlers
  const handleAreaToolToggle = () => {
    updateShowAreaTool(true)
    handleResetPoints()
  };

  const handleMeasureToolToggle = () => {
    updateShowAreaTool(false)
    handleResetPoints()
  };

  const handleCenterModel = () => {
    // This would need to be implemented in the ModalModelViewer
    console.log("Center model");
  };

  const handleResetPoints = () => {
    updatePoints([]);
    updateDistance(-1);
    updateAngle(-1);
    updateArea(-1);
    updatePerimeter(-1);
    updateDepthEstimation(-1);
    updateDepthStatus(DepthEstimationStatus.NOT_STARTED);
  };

  const handleCalculateDepth = () => {
    if (points.length >= 3 && area > 0) {
      updateDepthStatus(DepthEstimationStatus.IN_PROGRESS);
      // Simulate depth calculation - in real implementation this would be more complex
      setTimeout(() => {
        const estimatedDepth = Math.random() * 2 + 0.5; // Random depth between 0.5-2.5cm
        updateDepthEstimation(estimatedDepth);
        updateDepthStatus(DepthEstimationStatus.COMPLETED);
      }, 1000);
    }
  };

  const canCalculateDepth = showAreaTool && points.length >= 3 && area > 0;

  // Save current measurement data to the map
  const saveCurrentImageData = (imageUrl: string) => {
    if (!imageUrl) return;

    const dataToSave: ImageMeasurementData = {
      points,
      distance,
      angle,
      area,
      perimeter,
      depthEstimation,
      depthStatus,
      showAreaTool,
      cameraPosition: currentCameraPosition,
      cameraTarget: currentCameraTarget,
    };

    setImageDataMap(prev => new Map(prev.set(imageUrl, dataToSave)));
  };

  // Load measurement data for a specific image
  const loadImageData = (imageUrl: string) => {
    if (!imageUrl) return;

    const savedData = imageDataMap.get(imageUrl);
    if (savedData) {
      setPoints(savedData.points);
      setDistance(savedData.distance);
      setAngle(savedData.angle);
      setArea(savedData.area);
      setPerimeter(savedData.perimeter);
      setDepthEstimation(savedData.depthEstimation);
      setDepthStatus(savedData.depthStatus);
      setShowAreaTool(savedData.showAreaTool);
      setCurrentCameraPosition(savedData.cameraPosition);
      setCurrentCameraTarget(savedData.cameraTarget);
    } else {
      // Reset to defaults for new image
      setPoints([]);
      setDistance(-1);
      setAngle(-1);
      setArea(-1);
      setPerimeter(-1);
      setDepthEstimation(-1);
      setDepthStatus(DepthEstimationStatus.NOT_STARTED);
      setShowAreaTool(false);
      setCurrentCameraPosition(undefined);
      setCurrentCameraTarget(undefined);
    }
  };

  const getMeasurementText = (): string => {
    if (showAreaTool) {
      return area <= 0
        ? "Click to measure area"
        : `Area: ${area.toFixed(2)} cm² Perimeter: ${perimeter.toFixed(2)} cm`;
    } else {
      return distance < 0
        ? "Click to measure points"
        : `Distance: ${distance.toFixed(2)} cm, Angle: ${angle.toFixed(2)} degrees`;
    }
  };

  const getDepthEstimationText = (): string => {
    return depthEstimation > 0
      ? `Average Depth: ${depthEstimation.toFixed(2)} cm Estimated Volume: ${(area * depthEstimation).toFixed(2)} cm³`
      : "Click 'Calculate Depth' to estimate volume";
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth={false}
      fullWidth={false}
      sx={{
        "& .MuiPaper-root": {
          background: "#000000ff",
          width: widthSize,
          height: heightSize,
          minWidth: "unset",
          maxWidth: "unset",
          overflow: "hidden",
          margin: "auto",
          borderRadius: "12px",
          boxShadow:
            "0 25px 50px -12px rgba(0, 0, 0, 0.8), 0 0 0 1px rgba(255, 255, 255, 0.05)",
          padding: 0,
          "& > *": {
            margin: 0,
            padding: 0,
          },
        },
        "& .MuiDialog-container": {
          overflow: "hidden",
        },
      }}
    >
      <Stack
        height="100%"
        width="100%"
        sx={{ overflow: "hidden" }}
      >
        <ModelViewerControlBar
          title={title}
          showAreaTool={showAreaTool}
          pointsCount={points.length}
          canCalculateDepth={canCalculateDepth}
          depthStatus={depthStatus}
          disableDownload={imageSources.length < 1}
          onAreaToolToggle={handleAreaToolToggle}
          onMeasureToolToggle={handleMeasureToolToggle}
          onCenterModel={handleCenterModel}
          onResetPoints={handleResetPoints}
          onCalculateDepth={handleCalculateDepth}
          onDownload={acceptDownload}
          onClose={onClose}
        />

        <Stack
          sx={{
            position: "relative",
            flex: 1,
            overflow: "hidden",
          }}
        >
          {/* Navigation buttons */}
          <Button
            variant="text"
            disabled={imageSources.length < 2}
            onClick={onNavigatePrevious}
            sx={{
              position: "absolute",
              top: "0px",
              left: "0px",
              height: "100%",
              width: "100px",
              minWidth: "100px",
              zIndex: 9,
              background: "linear-gradient(to right, rgba(0, 0, 0, 0.2), transparent)",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              "&:hover": {
                background:
                  "linear-gradient(to right, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.1))",
                color: "white",
                "& .MuiSvgIcon-root": {
                  opacity: 1,
                },
              },
              "&:disabled": {
                background: "linear-gradient(to right, rgba(0, 0, 0, 0.1), transparent)",
                color: "rgba(255, 255, 255, 0.3)",
              },
              "& .MuiSvgIcon-root": {
                opacity: 0.3,
                fontSize: "3rem",
                transition: "opacity 0.2s ease-in-out",
              },
            }}
          >
            <ArrowBackIcon />
          </Button>

          <Button
            variant="text"
            disabled={imageSources.length < 2}
            onClick={onNavigateNext}
            sx={{
              position: "absolute",
              top: "0px",
              right: "0px",
              height: "100%",
              width: "100px",
              minWidth: "100px",
              zIndex: 9,
              background: "linear-gradient(to left, rgba(0, 0, 0, 0.2), transparent)",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              "&:hover": {
                background:
                  "linear-gradient(to left, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.1))",
                color: "white",
                "& .MuiSvgIcon-root": {
                  opacity: 1,
                },
              },
              "&:disabled": {
                background: "linear-gradient(to left, rgba(0, 0, 0, 0.1), transparent)",
                color: "rgba(255, 255, 255, 0.3)",
              },
              "& .MuiSvgIcon-root": {
                opacity: 0.3,
                fontSize: "3rem",
                transition: "opacity 0.2s ease-in-out",
              },
            }}
          >
            <ArrowForwardIcon />
          </Button>



          {/* Model pagination indicator */}
          {imageSources.length > 1 && (
            <Stack
              direction="row"
              spacing={1.5}
              justifyContent="center"
              alignItems="center"
              sx={{
                position: "absolute",
                bottom: "80px", // Move up to avoid status bar
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 10,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                borderRadius: "20px",
                paddingX: 2,
                paddingY: 1,
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                {imageSources.map((_, index) => (
                  <Button
                    key={index}
                    onClick={() => onImageIndexChange(index)}
                    sx={{
                      minWidth: "unset",
                      width: index === currentImageIndex ? "12px" : "8px",
                      height: index === currentImageIndex ? "12px" : "8px",
                      borderRadius: "50%",
                      padding: 0,
                      backgroundColor: index === currentImageIndex
                        ? "#2196f3"
                        : "rgba(255, 255, 255, 0.5)",
                      '&:hover': {
                        backgroundColor: index === currentImageIndex
                          ? "#1976d2"
                          : "rgba(255, 255, 255, 0.8)",
                      },
                      '&:active': {
                        transform: "scale(0.9)",
                      },
                      transition: "all 0.2s ease-in-out",
                    }}
                  />
                ))}
              </Stack>
              <Typography
                variant="caption"
                sx={{
                  color: "rgba(255, 255, 255, 0.8)",
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  userSelect: "none",
                  display: "flex",
                  alignItems: "center",
                  lineHeight: 1,
                }}
              >
                {currentImageIndex + 1}/{imageSources.length}
              </Typography>
            </Stack>
          )}

          {/* Bottom Status Bar / Footer */}
          <Stack
            direction="row"
            justifyContent="space-evenly"
            alignItems="center"
            spacing={1}
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              padding: "12px",
              textAlign: "center",
              borderBottomLeftRadius: "12px",
              borderBottomRightRadius: "12px",
              zIndex: 100,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "white",
                flexShrink: 0,
              }}
            >
              Left click and drag to rotate, right click and drag to pan, scroll to zoom
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "white",
                fontWeight: "medium",
              }}
            >
              {getMeasurementText()}
            </Typography>
            {showAreaTool && area > 0 && (
              <Typography
                variant="body2"
                sx={{
                  color: depthEstimation > 0 ? "#4caf50" : "rgba(255, 255, 255, 0.7)",
                  fontWeight: "medium",
                }}
              >
                {getDepthEstimationText()}
              </Typography>
            )}
          </Stack>

          <div style={{
            position: "relative",
            width: "100%",
            height: "100%",
            alignContent: "center",
            alignItems: "center",
            justifyContent: "center",
            justifyItems: "center"
          }}>
          </div>

          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: 0,
            fontSize: 0
          }}>
            <ModalModelViewer
              modelUrl={currentUrl}
              patientId={patientId}
              showAreaTool={showAreaTool}
              points={points}
              onPointsChange={updatePoints}
              onDistanceChange={updateDistance}
              onAngleChange={updateAngle}
              onAreaChange={updateArea}
              onPerimeterChange={updatePerimeter}
              cameraPosition={currentCameraPosition}
              cameraTarget={currentCameraTarget}
              onCameraChange={handleCameraChange}
            />
          </div>
        </Stack>
      </Stack>
    </Dialog>
  );
};

export default ModelViewerModal;
